import { Account } from 'common-entities';

import { getStore } from '../redux/store-utils.js';

import logger from './logger';

export const capabilities = {
  MACRO_EXP:                      'macro experience',
  CUSTOM_CODE:                    'custom css and js code',
  NEXT_GEN_EXP:                   'next gen experience',
  ACCOUNT_SETTINGS:               'Account Settings',
  USER_MGMT:                      'User Management',
  ANALYTICS:                      'Analytics',
  CONSUMER_SIGN_IN:               'Consumer sign in',
  WHITELABEL_APPS:                'White label Application',
  LABELS_AND_TRANSLATIONS:        'Labels and Translations',
  CHANNEL_MGMT:                   'channel management',
  MODULE_ADVANCED_SETTINGS:       'Module advanced settings',
  MODULE_DEVELOPER_OPTIONS:       'Module developer options',
  ACCOUNT_INTEGRATIONS:           'Account integration',
  DATA_PROTECTION:                'Data protection',
  ACCOUNT_SETTINGS_MENU:          'Account settings menu',
  LAYOUT_ROW_SETTINGS:            'Layout row settings',
  EMAIL_NOTIFICATIONS:            'Email Notifications',
  CLOUDFLARE_TURNSTILE_CAPTCHA:   'Cloudflare turnstile captcha',
  SMART_GOOGLE_CAPTCHA:           'Smart google captcha',
  ALWAYS_GOOGLE_CAPTCHA:          'Always google captcha',
  DATA_INTEGRATION:               'Data Integration',
  EMMA_DATA_INTEGRATION:          'EMMA data integration',
  CM_DATA_INTEGRATION:            'Campaign Monitor data integration',
  SEARCH_EXP_ACROSS_SUB_ACCOUNTS: 'Search experiences across sub accounts',
  SAILTHRU_ADVANCED_INTEGRATION:  'Sailthru advanced integration',
  OPENID_ONLOAD_INTEGRATION:      'OpenId On-Load advanced integration',
  EVENT_STREAM_INTEGRATION:       'Event stream integration',
  ADOBE_COMMERCE_PRODUCT_QUIZ:    'Adobe commerce integration',
  ADVOCACY_PACKAGE:               'ADVOCACY_PACKAGE', //the exact shape it has on the BE
  ZMP_ONLOAD_INTEGRATION:         'ZMP_ONLOAD_INTEGRATION',
  NEXTGEN_REMEMBER_ME:            'NextGen remember me',
  EXPRESS_APP_SWITCHER:           'Express App Switcher',
};

const CapabilityGate = createCapabilityGate(capabilities);
//any of the following capabilities should allow the 'account settings' menu to show and be used
const accountSettingsMenuDependencies = [
  'accountSettings',
  'userManagement',
  'analytics',
  'consumerSignIn',
  'whiteLabelApps',
  'labelsAndTranslations',
  'accountIntegration',
  'dataProtection',
  'channelManagement',
];

//checking if account capabilities has at least ONE of the needed dependencies
const accountSettingsMenuCapability = (accountCapabilities) =>
  _.some(accountSettingsMenuDependencies, (dependency) => accountCapabilities[dependency]);

export function createCapabilityGate(capabilities) {
  return (capability) => {
    const store = getStore();
    const _state = store.getState();
    const _account = Account.Selectors.activeAccount(_state);
    const validate = (capability) => {
      logger.warn(
        `Capability Gate => checking capability: ${capability} - unable to get the current account from state`,
        !_account
      );

      const _isCapabilityValid = {
        [capabilities.MACRO_EXP]: () => {
          return !!_account && _account.capabilities.macroExp;
        },
        [capabilities.CUSTOM_CODE]: () => {
          return !!_account && _account.capabilities.customCode;
        },
        [capabilities.ACCOUNT_SETTINGS]: () => {
          return !!_account && _account.capabilities.accountSettings;
        },
        [capabilities.USER_MGMT]: () => {
          return !!_account && _account.capabilities.userManagement;
        },
        [capabilities.ANALYTICS]: () => {
          return !!_account && _account.capabilities.analytics;
        },
        [capabilities.CONSUMER_SIGN_IN]: () => {
          return !!_account && _account.capabilities.consumerSignIn;
        },
        [capabilities.WHITELABEL_APPS]: () => {
          return !!_account && _account.capabilities.whiteLabelApps;
        },
        [capabilities.LABELS_AND_TRANSLATIONS]: () => {
          return !!_account && _account.capabilities.labelsAndTranslations;
        },
        [capabilities.NEXT_GEN_EXP]: () => {
          return !!_account && _account.capabilities.nextGenExp;
        },
        [capabilities.CHANNEL_MGMT]: () => {
          return !!_account && _account.capabilities.channelManagement;
        },
        [capabilities.MODULE_ADVANCED_SETTINGS]: () => {
          return !!_account && _account.capabilities.moduleAdvancedSettings;
        },
        [capabilities.MODULE_DEVELOPER_OPTIONS]: () => {
          return !!_account && _account.capabilities.moduleDeveloperOptions;
        },
        [capabilities.ACCOUNT_INTEGRATIONS]: () => {
          return !!_account && _account.capabilities.accountIntegration;
        },
        [capabilities.DATA_PROTECTION]: () => {
          return !!_account && _account.capabilities.dataProtection;
        },
        [capabilities.ACCOUNT_SETTINGS_MENU]: () => {
          return !!_account && accountSettingsMenuCapability(_account.capabilities);
        },
        [capabilities.LAYOUT_ROW_SETTINGS]: () => {
          return !!_account && _account.capabilities.layoutRowSettings;
        },
        [capabilities.EMAIL_NOTIFICATIONS]: () => {
          return !!_account && _account.capabilities.allowEmailNotifications;
        },
        [capabilities.CLOUDFLARE_TURNSTILE_CAPTCHA]: () => {
          return !!_account && _account.capabilities.cloudflareTurnstileCaptcha;
        },
        [capabilities.SMART_GOOGLE_CAPTCHA]: () => {
          return !!_account && _account.capabilities.smartGoogleCaptcha;
        },
        [capabilities.ALWAYS_GOOGLE_CAPTCHA]: () => {
          return !!_account && _account.capabilities.alwaysGoogleCaptcha;
        },
        [capabilities.EMMA_DATA_INTEGRATION]: () => {
          return !!_account && _account.capabilities.emmaDataIntegration;
        },
        [capabilities.CM_DATA_INTEGRATION]: () => {
          return !!_account && _account.capabilities.campaignMonitorDataIntegration;
        },
        [capabilities.OPENID_ONLOAD_INTEGRATION]: () => {
          return !!_account && _account.capabilities.openIdIntegration;
        },
        [capabilities.EVENT_STREAM_INTEGRATION]: () => {
          return !!_account && _account.capabilities.eventStreamIntegration;
        },
        [capabilities.DATA_INTEGRATION]: () => {
          // this capability checks both of available integrations at the moment, cause there is a generic code in a lot of places
          return (
            !!_account &&
            (_account.capabilities.emmaDataIntegration || _account.capabilities.campaignMonitorDataIntegration)
          );
        },
        [capabilities.SEARCH_EXP_ACROSS_SUB_ACCOUNTS]: () => {
          return !!_account && _account.capabilities.searchExperiencesAcrossSubaccounts;
        },
        [capabilities.ADOBE_COMMERCE_PRODUCT_QUIZ]: () => {
          return !!_account && _account.capabilities.adobeCommerceProductQuizIntegration;
        },
        [capabilities.ADVOCACY_PACKAGE]: () => {
          return !!_account && _account.capabilities.advocacyPackage;
        },
        [capabilities.ZMP_ONLOAD_INTEGRATION]: () => {
          return !!_account && _account.capabilities.zmpOnLoadIntegration;
        },
        [capabilities.NEXTGEN_REMEMBER_ME]: () => {
          return !!_account && _account.capabilities.nextgenRememberMe;
        },
        [capabilities.EXPRESS_APP_SWITCHER]: () => {
          return !!_account && _account.capabilities.expressAppSwitcher;
        },
      };

      return _isCapabilityValid[capability]() || false;
    };
    return validate(capability);
  };
}

export default CapabilityGate;
